export const DOPAMENU_STORAGE_KEY = 'dopatize:dopamenu:v1';
export const MAX_MENU_ITEMS = 50;
export const MAX_ACTIVITY_LENGTH = 160;
export const MAX_MENU_TITLE_LENGTH = 60;

export const MENU_CATEGORIES = [
  { id: 'starters', title: 'Starters', description: 'Quick little pick-me-ups', ideas: ['Stretch for a minute', 'Listen to a favorite song', 'Step outside'] },
  { id: 'mains', title: 'Mains', description: 'Make a little more time', ideas: ['Go for a walk', 'Make something creative', 'Cook a favorite meal'] },
  { id: 'sides', title: 'Sides', description: 'Pair with something you need to do', ideas: ['Play a favorite playlist', 'Make a cozy drink', 'Work beside a friend'] },
  { id: 'desserts', title: 'Desserts', description: 'Enjoy in small portions', ideas: ['Watch a funny video', 'Play a game for ten minutes', 'Enjoy a favorite snack'] },
  { id: 'specials', title: 'Specials', description: 'Something to look forward to', ideas: ['Plan a day trip', 'See a live show', 'Meet a friend for lunch'] },
];

export const MENU_THEMES = {
  mint: { name: 'Mint', paper: '#fffdf5', accent: '#b9efd3', secondary: '#ffd6b9', ink: '#203c35' },
  peach: { name: 'Peach', paper: '#fffaf2', accent: '#ffc4b2', secondary: '#d9d0f5', ink: '#49332f' },
  lavender: { name: 'Lavender', paper: '#fcfaff', accent: '#ded1ff', secondary: '#c4eddf', ink: '#38314d' },
};

export const initialDopamenu = () => ({ version: 1, title: 'My dopamine menu', theme: 'mint', items: [] });
const validCategory = (value) => MENU_CATEGORIES.some((category) => category.id === value);
const cleanActivity = (value) => typeof value === 'string' ? value.trim() : '';

export function restoreDopamenu(raw) {
  const defaults = initialDopamenu();
  try {
    const saved = JSON.parse(raw);
    if (saved?.version !== 1) return defaults;
    const seenIds = new Set();
    const seenTitles = new Set();
    const items = [];
    if (Array.isArray(saved.items)) saved.items.forEach((item) => {
      const title = cleanActivity(item?.title);
      if (typeof item?.id !== 'string' || !item.id || item.id.length > 100 || seenIds.has(item.id)
        || !validCategory(item.category) || !title || title.length > MAX_ACTIVITY_LENGTH
        || seenTitles.has(title.toLowerCase()) || items.length >= MAX_MENU_ITEMS) return;
      seenIds.add(item.id);
      seenTitles.add(title.toLowerCase());
      items.push({ id: item.id, title, category: item.category });
    });
    return { ...defaults, items,
      title: typeof saved.title === 'string' ? saved.title.slice(0, MAX_MENU_TITLE_LENGTH) : defaults.title,
      theme: Object.prototype.hasOwnProperty.call(MENU_THEMES, saved.theme) ? saved.theme : defaults.theme };
  } catch (_error) {
    return defaults;
  }
}

export function dopamenuReducer(state, action) {
  switch (action.type) {
    case 'title':
      return typeof action.value === 'string' ? { ...state, title: action.value.slice(0, MAX_MENU_TITLE_LENGTH) } : state;
    case 'theme':
      return Object.prototype.hasOwnProperty.call(MENU_THEMES, action.value) ? { ...state, theme: action.value } : state;
    case 'category': {
      if (!validCategory(action.category) || !Array.isArray(action.entries)) return state;
      const others = state.items.filter((item) => item.category !== action.category);
      if (others.length + action.entries.length > MAX_MENU_ITEMS) return state;
      const ids = new Set(others.map((item) => item.id));
      const titles = new Set(others.map((item) => item.title.toLowerCase()));
      const entries = [];
      for (const entry of action.entries) {
        const title = cleanActivity(entry?.title);
        if (typeof entry?.id !== 'string' || !entry.id || entry.id.length > 100 || ids.has(entry.id)
          || !title || title.length > MAX_ACTIVITY_LENGTH || titles.has(title.toLowerCase())) return state;
        ids.add(entry.id);
        titles.add(title.toLowerCase());
        entries.push({ id: entry.id, title, category: action.category });
      }
      return { ...state, items: [...others, ...entries] };
    }
    case 'save': {
      const title = cleanActivity(action.title);
      const existing = state.items.find((item) => item.id === action.id);
      if (typeof action.id !== 'string' || !action.id || action.id.length > 100 || !title
        || title.length > MAX_ACTIVITY_LENGTH || !validCategory(action.category)
        || (!existing && state.items.length >= MAX_MENU_ITEMS)
        || state.items.some((item) => item.id !== action.id && item.title.toLowerCase() === title.toLowerCase())) return state;
      const item = { id: action.id, title, category: action.category };
      return { ...state, items: existing ? state.items.map((entry) => entry.id === action.id ? item : entry) : [...state.items, item] };
    }
    case 'remove':
      return { ...state, items: state.items.filter((item) => item.id !== action.id) };
    case 'restore':
      return restoreDopamenu(action.raw);
    default:
      return state;
  }
}
